import React, { useState, useMemo } from 'react';
import { Card, Text, Flex, Badge, Grid } from '@tremor/react';
import {
  CurrencyDollarIcon,
  ChartBarIcon,
  UserGroupIcon,
  MegaphoneIcon,
  ArrowTrendingUpIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import MetricInfo from './MetricInfo';

/**
 * MarketingEfficiency Component
 * Displays paid channel performance metrics from v_marketing_roi_unified.
 * Channels: Google Ads, Facebook (Meta), LinkedIn.
 * UI features per-channel tabs and a unified "All" view.
 * Data source: /api/marketing-efficiency (props.data)
 */

const CHANNEL_CONFIG = {
  all:      { label: 'All Channels', short: 'All',      color: 'pink' },
  google:   { label: 'Google Ads',   short: 'Google',   color: 'red' },
  facebook: { label: 'Meta',         short: 'Meta',     color: 'blue' },
  linkedin: { label: 'LinkedIn',     short: 'LinkedIn', color: 'indigo' },
};

const formatCurrency = (value) => {
  if (value === null || value === undefined) return 'N/A';
  if (value >= 1000000) return `$${(value / 1000000).toFixed(2)}M`;
  if (value >= 1000) return `$${(value / 1000).toFixed(1)}K`;
  return `$${Math.round(value).toLocaleString()}`;
};

const MarketingEfficiency = ({ data }) => {
  // All hooks must be called at the top, before any conditional returns,
  // to satisfy the React Rules of Hooks. Defaults guard against undefined data.
  const [activeTab, setActiveTab] = useState('all');

  const summary = data?.summary || null;
  const campaigns = data?.campaigns || [];
  const by_channel = data?.by_channel || {};

  // Stable channel ordering: Google, Facebook, LinkedIn, then anything else
  const channelsPresent = useMemo(() => {
    const names = Object.keys(by_channel).filter(c => (by_channel[c]?.total_spend > 0) || (by_channel[c]?.campaign_count > 0));
    const order = ['google', 'facebook', 'linkedin'];
    return [...order.filter(c => names.includes(c)), ...names.filter(c => !order.includes(c))];
  }, [by_channel]);

  // Filter campaigns by active tab (computed before early returns for hook stability)
  const visibleCampaigns = useMemo(() => {
    const list = activeTab === 'all' ? campaigns : campaigns.filter(c => c.channel === activeTab);
    return list.filter(c => c.total_spend > 0).sort((a, b) => b.total_spend - a.total_spend);
  }, [campaigns, activeTab]);

  // Loading state (after hooks)
  if (!summary) {
    return (
      <Card className="bg-white/80 backdrop-blur-2xl rounded-3xl border border-white/20 shadow-xl">
        <Flex justifyContent="between" alignItems="center" className="mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-pink-50 rounded-xl shadow-sm">
              <MegaphoneIcon className="h-6 w-6 text-[#FF3489]" />
            </div>
            <div>
              <Text className="text-xl font-bold text-gray-900">Marketing Efficiency</Text>
              <Text className="text-sm text-gray-500">Loading paid channel data...</Text>
            </div>
          </div>
          <Badge color="gray" size="lg">Loading</Badge>
        </Flex>
        <div className="bg-gray-50/50 rounded-2xl p-5">
          <Text className="text-sm text-gray-400">Fetching paid channel performance...</Text>
        </div>
      </Card>
    );
  }

  // Resolve the active view's totals
  const view = activeTab === 'all' ? summary : (by_channel[activeTab] || {});
  const {
    total_spend = 0,
    total_pipeline_value = 0,
    total_arr_generated = 0,
    total_attributed_deals = 0,
    total_won_deals = 0,
    overall_cpa = null,
    overall_roas = 0,
    has_spend = false,
    has_attribution = false,
  } = view;

  // No spend at all anywhere - show monitoring state
  if (!summary.has_spend && channelsPresent.length === 0) {
    return (
      <Card className="bg-blue-50/80 backdrop-blur-2xl rounded-3xl border border-blue-200/50 shadow-xl">
        <Flex justifyContent="between" alignItems="center" className="mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-white/70 rounded-xl shadow-sm">
              <MegaphoneIcon className="h-6 w-6 text-[#FF3489]" />
            </div>
            <div>
              <Text className="text-xl font-bold text-gray-900">Marketing Efficiency</Text>
              <Text className="text-sm text-gray-500">No paid channel spend recorded yet</Text>
            </div>
          </div>
          <Badge color="blue" size="lg">Monitoring</Badge>
        </Flex>
        <div className="p-4 bg-white/70 rounded-xl">
          <Flex alignItems="center" className="gap-2">
            <ClockIcon className="h-5 w-5 text-blue-500" />
            <Text className="text-sm text-blue-700">
              Waiting for paid channel data. Google Ads, Meta, and LinkedIn ETLs should report within 24 hours of campaign launch.
            </Text>
          </Flex>
        </div>
      </Card>
    );
  }

  // Status pill for the currently selected tab
  const getStatus = () => {
    if (total_arr_generated > 0 && total_spend > 0) {
      return { label: 'Generating Revenue', color: 'emerald', bg: 'bg-emerald-50/80', border: 'border-emerald-200/50' };
    }
    if (total_attributed_deals > 0) {
      return { label: 'Leads in Pipeline', color: 'amber', bg: 'bg-amber-50/80', border: 'border-amber-200/50' };
    }
    if (total_spend > 0) {
      return { label: 'Spending - No Attribution', color: 'orange', bg: 'bg-orange-50/80', border: 'border-orange-200/50' };
    }
    return { label: 'Monitoring', color: 'blue', bg: 'bg-blue-50/80', border: 'border-blue-200/50' };
  };
  const status = getStatus();

  const tabsToShow = ['all', ...channelsPresent];

  return (
    <Card className={`${status.bg} backdrop-blur-2xl rounded-3xl border ${status.border} shadow-xl`}>
      {/* Header */}
      <Flex justifyContent="between" alignItems="center" className="mb-4">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-white/70 rounded-xl shadow-sm">
            <MegaphoneIcon className="h-6 w-6 text-[#FF3489]" />
          </div>
          <div>
            <Text className="text-xl font-bold text-gray-900">Marketing Efficiency</Text>
            <Text className="text-sm text-gray-500">
              Paid channels • {channelsPresent.length} active channel{channelsPresent.length !== 1 ? 's' : ''} • {summary.campaign_count} total campaign{summary.campaign_count !== 1 ? 's' : ''}
            </Text>
          </div>
        </div>
        <Badge color={status.color} size="lg">
          {status.label}
        </Badge>
      </Flex>

      {/* Channel Tab Strip */}
      <div className="flex gap-1.5 mb-5 overflow-x-auto pb-1">
        {tabsToShow.map(tab => {
          const isActive = activeTab === tab;
          const cfg = CHANNEL_CONFIG[tab] || { short: tab, label: tab };
          const channelSpend = tab === 'all' ? summary.total_spend : (by_channel[tab]?.total_spend || 0);
          return (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all whitespace-nowrap ${
                isActive
                  ? 'bg-gray-900 text-white shadow-md'
                  : 'bg-white/60 text-gray-700 hover:bg-white/90 border border-gray-200'
              }`}
            >
              {cfg.short}
              <span className={`ml-2 text-xs ${isActive ? 'text-gray-300' : 'text-gray-400'} font-mono`}>
                {formatCurrency(channelSpend)}
              </span>
            </button>
          );
        })}
      </div>

      {/* Metrics Grid */}
      <Grid numItems={1} numItemsSm={3} className="gap-4">
        {/* Total Ad Spend */}
        <div className="bg-white/70 rounded-2xl p-5 shadow-sm">
          <Flex alignItems="start" className="gap-3 mb-3">
            <div className="p-2 bg-pink-100 rounded-lg">
              <CurrencyDollarIcon className="h-5 w-5 text-[#FF3489]" />
            </div>
            <div className="flex-1">
              <Flex justifyContent="between" alignItems="start">
                <Text className="text-xs text-gray-500 uppercase tracking-wide font-semibold">
                  Total Ad Spend
                </Text>
                <MetricInfo id="Total Ad Spend" />
              </Flex>
            </div>
          </Flex>
          <Text className="text-3xl font-bold text-gray-900 font-mono">
            {formatCurrency(total_spend)}
          </Text>
          <Text className="text-xs text-gray-500 mt-1">
            {visibleCampaigns.length} active campaign{visibleCampaigns.length !== 1 ? 's' : ''} in view
          </Text>
        </div>

        {/* Marketing-Sourced Pipeline */}
        <div className="bg-white/70 rounded-2xl p-5 shadow-sm">
          <Flex alignItems="start" className="gap-3 mb-3">
            <div className="p-2 bg-cyan-100 rounded-lg">
              <ChartBarIcon className="h-5 w-5 text-[#00CBC0]" />
            </div>
            <div className="flex-1">
              <Flex justifyContent="between" alignItems="start">
                <Text className="text-xs text-gray-500 uppercase tracking-wide font-semibold">
                  Marketing Pipeline
                </Text>
                <MetricInfo id="Marketing Pipeline" />
              </Flex>
            </div>
          </Flex>
          <Text className="text-3xl font-bold text-gray-900 font-mono">
            {formatCurrency(total_pipeline_value)}
          </Text>
          <Text className="text-xs text-gray-500 mt-1">
            {total_attributed_deals} attributed deal{total_attributed_deals !== 1 ? 's' : ''}
          </Text>
        </div>

        {/* CPA */}
        <div className="bg-white/70 rounded-2xl p-5 shadow-sm">
          <Flex alignItems="start" className="gap-3 mb-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <UserGroupIcon className="h-5 w-5 text-purple-600" />
            </div>
            <div className="flex-1">
              <Flex justifyContent="between" alignItems="start">
                <Text className="text-xs text-gray-500 uppercase tracking-wide font-semibold">
                  Cost Per Acquisition
                </Text>
                <MetricInfo id="Cost Per Acquisition" />
              </Flex>
            </div>
          </Flex>
          <Text className="text-3xl font-bold text-gray-900 font-mono">
            {overall_cpa ? formatCurrency(overall_cpa) : 'N/A'}
          </Text>
          <Text className="text-xs text-gray-500 mt-1">
            {total_won_deals > 0 ? `${total_won_deals} won deal${total_won_deals !== 1 ? 's' : ''}` : 'Awaiting first conversion'}
          </Text>
        </div>
      </Grid>

      {/* Status Message for spending-no-attribution state */}
      {!has_attribution && has_spend && (
        <div className="mt-4 p-4 bg-orange-50/50 rounded-xl border border-orange-100">
          <Flex alignItems="center" className="gap-2">
            <ClockIcon className="h-5 w-5 text-orange-500" />
            <Text className="text-sm text-orange-700">
              {activeTab === 'all' ? 'Campaigns' : CHANNEL_CONFIG[activeTab]?.label || activeTab} active with <strong>{formatCurrency(total_spend)}</strong> in spend.
              No attributed deals yet. Verify UTM tracking is firing on destination URLs.
            </Text>
          </Flex>
        </div>
      )}

      {/* ROAS Badge */}
      {overall_roas > 0 && (
        <div className="mt-4 p-4 bg-emerald-50/50 rounded-xl border border-emerald-100">
          <Flex justifyContent="between" alignItems="center">
            <Flex alignItems="center" className="gap-2">
              <ArrowTrendingUpIcon className="h-5 w-5 text-emerald-600" />
              <Text className="text-sm text-emerald-700">
                Return on Ad Spend (ROAS)
              </Text>
              <MetricInfo id="ROAS" />
            </Flex>
            <Text className="text-2xl font-bold text-emerald-700 font-mono">
              {overall_roas.toFixed(2)}x
            </Text>
          </Flex>
          <Text className="text-xs text-emerald-600 mt-2">
            Generated {formatCurrency(total_arr_generated)} ARR from {formatCurrency(total_spend)} ad spend
          </Text>
        </div>
      )}

      {/* Campaign Table: visible when viewing a single channel or "All" with campaigns */}
      {visibleCampaigns.length > 0 && (
        <div className="mt-5 pt-4 border-t border-gray-200/60">
          <Text className="text-xs text-gray-500 uppercase tracking-wide font-semibold mb-3">
            Campaigns ({visibleCampaigns.length})
          </Text>
          <div className="space-y-1.5">
            {visibleCampaigns.slice(0, 12).map(c => {
              const cfg = CHANNEL_CONFIG[c.channel] || { short: c.channel };
              return (
                <div key={`${c.channel}-${c.campaign_id}`} className="flex justify-between items-center px-3 py-2 bg-white/60 rounded-lg border border-gray-100">
                  <div className="flex items-center gap-2 min-w-0">
                    {activeTab === 'all' && (
                      <Badge color={cfg.color || 'gray'} size="xs">{cfg.short}</Badge>
                    )}
                    <div className="min-w-0">
                      <Text className="text-sm font-semibold text-gray-800 truncate">{c.campaign_name}</Text>
                      <Text className="text-xs text-gray-500">
                        {c.attributed_deals} deal{c.attributed_deals !== 1 ? 's' : ''} • {c.won_deals} won
                        {c.ctr_pct > 0 && ` • CTR ${c.ctr_pct.toFixed(2)}%`}
                      </Text>
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0 ml-3">
                    <Text className="text-sm font-bold text-gray-900 font-mono">{formatCurrency(c.total_spend)}</Text>
                    {c.arr_generated > 0 && (
                      <Text className="text-xs text-emerald-600 font-mono">+{formatCurrency(c.arr_generated)} ARR</Text>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </Card>
  );
};

export default MarketingEfficiency;
